import { useAuthFetch } from "@/hooks/data/useAuthFetch";
import { tagQueryKeys } from "@/hooks/data/v2/query-keys";
import { parseResponse } from "@/lib/query-helper";
import { APIResponse, PaginatedResponse, SearchFilter } from "@simplerealtybits/srb-types";
import { TagCreateDto } from "@t/Common/Dtos/Tags/TagCreateDto";
import { TagGetDto } from "@t/Common/Dtos/Tags/TagGetDto";
import { TagUpdateDto } from "@t/Common/Dtos/Tags/TagUpdateDto";
import {
    QueryClient,
    UndefinedInitialDataOptions,
    useMutation,
    UseMutationOptions,
    useQuery,
} from "@tanstack/react-query";

type TagSearchOptions = Partial<
    UndefinedInitialDataOptions<PaginatedResponse<TagGetDto>, Error, TagGetDto[], any>
>;

export function useTagSearchQuery(
    filter: SearchFilter,
    options?: TagSearchOptions,
    queryClient?: QueryClient
) {
    const authFetch = useAuthFetch();

    return useQuery(
        {
            queryKey: tagQueryKeys.search(filter),
            queryFn: async (): Promise<PaginatedResponse<TagGetDto>> => {
                const res = await authFetch(`/api/v1/tags/search`, {
                    method: "POST",
                    body: JSON.stringify(filter),
                });
                return await parseResponse(res);
            },
            select: (data) => data.data,
            ...options,
        },
        queryClient
    );
}

type TagByIdOptions = Partial<
    UndefinedInitialDataOptions<APIResponse<TagGetDto>, Error, TagGetDto, any>
>;

export function useTagByIdQuery(
    tagId: number,
    options?: TagByIdOptions,
    queryClient?: QueryClient
) {
    const authFetch = useAuthFetch();
    return useQuery(
        {
            queryKey: tagQueryKeys.getById(tagId),
            queryFn: async (): Promise<APIResponse<TagGetDto>> => {
                if (!tagId) throw new Error("Tag ID cannot be null");

                let res = await authFetch(`/api/v1/tags/${tagId}`);
                return await parseResponse(res);
            },
            enabled: !!tagId,
            select: (response) => response.data,
            ...options,
        },
        queryClient
    );
}

type TagCreateOptions = Partial<
    UseMutationOptions<APIResponse<TagGetDto>, Error, TagCreateDto, unknown>
>;

export function useTagCreateMutation(options?: TagCreateOptions, queryClient?: QueryClient) {
    const authFetch = useAuthFetch();
    return useMutation(
        {
            mutationFn: async (newTag: TagCreateDto): Promise<APIResponse<TagGetDto>> => {
                const res = await authFetch(`/api/v1/tags`, {
                    method: "POST",
                    body: JSON.stringify(newTag),
                });
                return await parseResponse(res);
            },
            ...options,
        },
        queryClient
    );
}

type TagUpdateOptions = Partial<
    UseMutationOptions<APIResponse<TagGetDto>, Error, TagUpdateDto, unknown>
>;

export function useTagUpdateMutation(options?: TagUpdateOptions, queryClient?: QueryClient) {
    const authFetch = useAuthFetch();
    return useMutation(
        {
            mutationFn: async (updatedTag: TagUpdateDto): Promise<APIResponse<TagGetDto>> => {
                const res = await authFetch(`/api/v1/tags/${updatedTag.id}`, {
                    method: "PUT",
                    body: JSON.stringify(updatedTag),
                });
                return await parseResponse(res);
            },
            ...options,
        },
        queryClient
    );
}

type TagDeleteOptions = Partial<UseMutationOptions<APIResponse<string>, Error, number, unknown>>;

export function useTagDeleteMutation(options?: TagDeleteOptions, queryClient?: QueryClient) {
    const authFetch = useAuthFetch();
    return useMutation(
        {
            mutationFn: async (tagId: number): Promise<APIResponse<string>> => {
                const res = await authFetch(`/api/v1/tags/${tagId}`, {
                    method: "DELETE",
                });
                return await parseResponse(res);
            },
            ...options,
        },
        queryClient
    );
}
